/**
 * Formatting helpers for timestamps read from the server (audit_log.created_at,
 * users.last_login, registration_requests.created_at, etc.) — all stored via
 * SQLite's CURRENT_TIMESTAMP/datetime('now'), which is always UTC but comes
 * back as a bare "YYYY-MM-DD HH:MM:SS" string with no timezone marker.
 *
 * `new Date("YYYY-MM-DD HH:MM:SS")` (space-separated, no "Z") is NOT
 * recognized by the JS Date parser as UTC — it's parsed as local time
 * instead, so the raw UTC clock digits get echoed back unconverted. For a
 * facility outside UTC (e.g. Alaska, UTC-8/-9) that makes every logged time
 * appear 8-9 hours later than it actually was. parseServerTimestamp() below
 * normalizes the string to real ISO 8601 UTC ("...THH:MM:SSZ") first so the
 * browser converts it to local time correctly.
 */

/**
 * Parses a server timestamp string into a Date, correcting for SQLite's
 * unmarked-UTC format. Already-marked strings (has "Z" or a +/-HH:MM
 * offset) are trusted as-is and passed straight to the Date constructor.
 * @param {string} value
 * @returns {Date|null}
 */
export function parseServerTimestamp(value) {
  if (!value) return null;
  const hasTimezoneMarker = /[Zz]|[+-]\d{2}:?\d{2}$/.test(value);
  const normalized = hasTimezoneMarker ? value : `${value.replace(' ', 'T')}Z`;
  const date = new Date(normalized);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Formats a server timestamp as a localized date + time string
 * (e.g. "7/25/2026, 1:48:07 AM" in the browser's local timezone).
 */
export function formatServerDateTime(value, fallback = '') {
  const date = parseServerTimestamp(value);
  return date ? date.toLocaleString() : fallback;
}

/**
 * Formats a server timestamp as a localized date-only string
 * (e.g. "7/25/2026").
 */
export function formatServerDate(value, fallback = '') {
  const date = parseServerTimestamp(value);
  return date ? date.toLocaleDateString() : fallback;
}
